import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Schedule,
  Work,
  Person,
} from '@mui/icons-material';
import { API_BASE_URL } from '../services/config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface TimesheetFormData {
  projectId: string;
  date: string;
  startTime: string;
  endTime: string;
  breakDuration: string;
  description: string;
  status: string;
  employeeId?: string;
}

interface Project {
  _id: string;
  name: string;
}

interface EmployeeOption {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
}

const TimesheetCreate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const [formData, setFormData] = useState<TimesheetFormData>({
    projectId: '',
    date: new Date().toISOString().split('T')[0],
    startTime: '08:00',
    endTime: '17:00',
    breakDuration: '60',
    description: '',
    status: 'draft',
  });

  const [errors, setErrors] = useState<Partial<TimesheetFormData>>({});

  const statusOptions = [
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  useEffect(() => {
    fetchProjects();
    if (user?.role === 'admin') {
      fetchEmployees();
    }
    // If editing, load existing timesheet to prefill
    const editId = searchParams.get('id');
    if (editId) {
      loadTimesheetForEdit(editId);
    }
  }, []);

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token');
  const response = await fetch(`${API_BASE_URL}/projects`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }

      const data = await response.json();
      setProjects(data.data || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      setLoadingEmployees(true);
      const response = await api.get('/users', {
        params: { isActive: true, limit: 100 },
      });
      const list = Array.isArray(response.data?.users) ? response.data.users : [];
      setEmployees(list);
    } catch (err) {
      console.error('Error fetching employees:', err);
      setEmployees([]);
    } finally {
      setLoadingEmployees(false);
    }
  };

  const loadTimesheetForEdit = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get(`/timesheets/${id}`);
      const ts = response.data?.data;
      if (ts) {
        setFormData((prev) => ({
          ...prev,
          projectId: ts.project?._id || ts.project || prev.projectId,
          date: ts.date ? new Date(ts.date).toISOString().split('T')[0] : prev.date,
          description: ts.notes || ts.description || prev.description,
          status: ts.status || prev.status,
          employeeId: ts.employee?._id || ts.employee || prev.employeeId,
        }));
      }
    } catch (err: any) {
      console.error('Error loading timesheet for edit:', err);
      setError(err.response?.data?.message || 'Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  const calculateHours = (start: string, end: string, breakMinutes: number): number => {
    const startTime = new Date(`2000-01-01T${start}:00`);
    const endTime = new Date(`2000-01-01T${end}:00`);
    const diffMs = endTime.getTime() - startTime.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    return Math.max(0, diffHours - (breakMinutes / 60));
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<TimesheetFormData> = {};

    if (!formData.projectId) {
      newErrors.projectId = 'Project is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.startTime) {
      newErrors.startTime = 'Start time is required';
    }

    if (!formData.endTime) {
      newErrors.endTime = 'End time is required';
    } else if (formData.startTime && formData.endTime <= formData.startTime) {
      newErrors.endTime = 'End time must be after start time';
    }

    if (!formData.breakDuration || parseInt(formData.breakDuration) < 0) {
      newErrors.breakDuration = 'Valid break duration is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Work description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof TimesheetFormData) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSelectChange = (field: keyof TimesheetFormData) => (
    event: any
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Map form fields to backend expectations
      const payload = {
        project: formData.projectId,
        // Send ISO date to avoid timezone parsing issues server-side
        date: new Date(formData.date).toISOString(),
        status: formData.status,
        // Optional fields that backend may accept; safe to include
        notes: formData.description,
        // Allow admin to create timesheets for selected employee
        ...(user?.role === 'admin' && formData.employeeId ? { employee: formData.employeeId } : {})
      };

      const editId = searchParams.get('id');
      let idToNavigate = '';
      if (editId) {
        const updatePayload = { ...payload };
        // For update, do not change project/employee per backend rules
        delete (updatePayload as any).project;
        delete (updatePayload as any).employee;
        const response = await api.put(`/timesheets/${editId}`, updatePayload);
        const updated = response.data;
        idToNavigate = updated?.data?._id || updated?._id || editId;
        setSuccess(true);
      } else {
        const response = await api.post('/timesheets', payload);
        const created = response.data;
        idToNavigate = created?.data?._id || created?._id || created?.id;
        setSuccess(true);
      }

      // Redirect to timesheet detail page after a short delay
      setTimeout(() => {
        navigate(`/timesheets/${idToNavigate}`);
      }, 800);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to create timesheet';
      const details = Array.isArray(err?.response?.data?.errors) ? err.response.data.errors : [];
      setError(details.length ? `${msg}: ${details.join('; ')}` : msg);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/timesheets');
  };

  const totalHours = calculateHours(
    formData.startTime,
    formData.endTime,
    parseInt(formData.breakDuration) || 0
  );

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1">
          {searchParams.get('id') ? 'Edit Timesheet' : 'Create New Timesheet'}
        </Typography>
      </Box>

      {/* Success Alert */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Timesheet created successfully! Redirecting to timesheet details...
        </Alert>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Form */}
      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              {/* Project and Date */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <FormControl sx={{ minWidth: 200, flex: 1 }} error={!!errors.projectId}>
                  <InputLabel>Project *</InputLabel>
                  <Select
                    value={formData.projectId}
                    label="Project *"
                    onChange={handleSelectChange('projectId')}
                    disabled={loadingProjects}
                    startAdornment={<Work sx={{ mr: 1, color: 'action.active' }} />}
                  >
                    {projects.map((project) => (
                      <MenuItem key={project._id} value={project._id}>
                        {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.projectId && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                      {errors.projectId}
                    </Typography>
                  )}
                </FormControl>

                {user?.role === 'admin' && (
                  <FormControl sx={{ minWidth: 220, flex: 1 }}>
                    <InputLabel>Employee *</InputLabel>
                    <Select
                      value={formData.employeeId || ''}
                      label="Employee *"
                      onChange={handleSelectChange('employeeId')}
                      disabled={loadingEmployees}
                      startAdornment={<Person sx={{ mr: 1, color: 'action.active' }} />}
                    >
                      {employees.map((e) => (
                        <MenuItem key={e._id} value={e._id}>
                          {e.firstName} {e.lastName} ({e.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <TextField
                  label="Date"
                  type="date"
                  value={formData.date}
                  onChange={handleInputChange('date')}
                  error={!!errors.date}
                  helperText={errors.date}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: <Schedule sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                  sx={{ minWidth: 200, flex: 1 }}
                  required
                />
              </Box>

              {/* Time Fields */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Start Time"
                  type="time"
                  value={formData.startTime}
                  onChange={handleInputChange('startTime')}
                  error={!!errors.startTime}
                  helperText={errors.startTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 150 }}
                  required
                />

                <TextField
                  label="End Time"
                  type="time"
                  value={formData.endTime}
                  onChange={handleInputChange('endTime')}
                  error={!!errors.endTime}
                  helperText={errors.endTime}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{ minWidth: 150 }}
                  required
                />

                <TextField
                  label="Break Duration (minutes)"
                  type="number"
                  value={formData.breakDuration}
                  onChange={handleInputChange('breakDuration')}
                  error={!!errors.breakDuration}
                  helperText={errors.breakDuration}
                  sx={{ minWidth: 180 }}
                  required
                />
              </Box>

              {/* Total Hours Display */}
              <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="h6" color="primary">
                  Total Hours: {totalHours.toFixed(2)}
                </Typography>
              </Box>

              {/* Description */}
              <TextField
                fullWidth
                label="Work Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleInputChange('description')}
                error={!!errors.description}
                helperText={errors.description}
                placeholder="Describe the work performed during this time period..."
                required
              />

              {/* Status */}
              <FormControl sx={{ maxWidth: 200 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={handleSelectChange('status')}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Submit Buttons */}
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                >
                  {loading ? (searchParams.get('id') ? 'Updating...' : 'Creating...') : (searchParams.get('id') ? 'Update Timesheet' : 'Create Timesheet')}
                </Button>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default TimesheetCreate;